/**
 * A refused share operation inside its dialog: the typed IPC message (for an Entra share-administration 403, main's
 * `shareAdminDenial` sentence), the `needs-role` chip worded like the storage browser's error state, and — for a
 * conflict whose fix is `reload` (`QuotaChanged`, `SnapshotsChanged`) — a Reload button. Phrasing elements only, so it
 * also sits inside the confirm dialog's description paragraph.
 */
import { Button } from '../../../../../components/ui/button';
import type { StorageOperationError } from '../../../../../types/azureStorage';

function roleChipText(error: StorageOperationError): string | undefined {
  if (error.fix?.kind !== 'needs-role') return undefined;
  return `Needs the ${error.fix.role} role (Azure portal → the storage account → Access control (IAM))`;
}

type Props = { error: StorageOperationError; testId: string; onReload?: () => void };

export function ShareErrorNotice({ error, testId, onReload }: Props) {
  const chip = roleChipText(error);
  return (
    <span className="block rounded border border-destructive/50 bg-destructive/10 p-2 text-xs text-foreground" role="alert" data-testid={testId}>
      <span className="block">{error.message}</span>
      {chip && <span className="mt-1 block text-muted-foreground" data-testid="share-error-role">{chip}</span>}
      {onReload && error.fix?.kind === 'reload' && (
        <Button type="button" size="xs" variant="outline" className="mt-2" onClick={onReload} data-testid={`${testId}-reload`}>Reload</Button>
      )}
    </span>
  );
}
